import { Email as EmailIcon } from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Container,
    List,
    ListItem,
    ListItemText,
    Typography,
    useTheme
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ApiClient, { HttpResponse } from "../config/ApiClient";
import { useAppContext } from "../context/AppContext";
import { ApiListResponse, Users } from "../object-actions/types/types";
import HeroSection from "../theme/HeroSection";
const CoachProfileScreen: React.FC = () => {
    const { username } = useParams<{ username: string }>();
    const { organization } = useAppContext();
    const theme = useTheme();
    const orgSlug = organization?.slug || "smsp";

    const [coach, setCoach] = useState<Users | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!username) return;

        const fetchCoach = async () => {
            try {
                setLoading(true);
                setError(null);
                const response: HttpResponse<ApiListResponse<"Users">> = await ApiClient.get(
                    `/api/users/search-coaches?organization=${orgSlug}&detail=1&q=${encodeURIComponent(username)}`
                );
                if (response.success && response.data?.results) {
                    const match = response.data.results.find(
                        (u) => u.username?.toLowerCase() === username.toLowerCase()
                    );
                    if (match) {
                        setCoach(match);
                    } else {
                        setError("Coach not found.");
                    }
                } else {
                    setError(response.error || "Failed to load coach.");
                }
            } catch (err) {
                console.error("Error fetching coach:", err);
                setError("Failed to load coach. Please try again later.");
            } finally {
                setLoading(false);
            }
        };

        fetchCoach();
    }, [username, orgSlug]);

    const displayName = coach
        ? [coach.first_name, coach.last_name].filter(Boolean).join(" ") || coach.username
        : "";
    const tagline = coach?.summary || "Discipline. Confidence. Next-Level Performance.";

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress size={60} />
            </Box>
        );
    }

    if (error || !coach) {
        return (
            <Container maxWidth="md" sx={{ py: 8 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error || "Coach not found."}
                </Alert>
                <Button component={Link} to="/who-we-are">
                    Back to About
                </Button>
            </Container>
        );
    }

    const heroImageUrl = coach.cover_photo || coach.photo;

    return (
        <Box sx={{ pb: 8 }}>
            <HeroSection imageUrl={heroImageUrl} minHeight="80vh">
                <Box
                    sx={{
                        position: "relative",
                        zIndex: 1,
                        width: "100%",
                        maxWidth: 720,
                        px: { xs: 2, sm: 3, md: 6 },
                        pb: { xs: 4, sm: 5 },
                        pt: 4
                    }}
                >
                    <Typography
                        variant="h3"
                        component="h1"
                        sx={{
                            fontWeight: 700,
                            color: "#fff",
                            textShadow: "0 2px 8px rgba(0,0,0,0.5)",
                            mb: 1,
                            lineHeight: 1.2
                        }}
                    >
                        Coach {displayName}
                    </Typography>
                    <Typography
                        variant="h5"
                        component="h2"
                        sx={{
                            color: "primary.light",
                            fontWeight: 600,
                            mb: 2,
                            textShadow: "0 1px 4px rgba(0,0,0,0.5)"
                        }}
                    >
                        {tagline}
                    </Typography>
                    {coach.bio && (
                        <Typography
                            variant="body1"
                            sx={{
                                color: "rgba(255,255,255,0.95)",
                                maxWidth: 520,
                                lineHeight: 1.7,
                                textShadow: "0 1px 3px rgba(0,0,0,0.5)"
                            }}
                        >
                            {coach.bio}
                        </Typography>
                    )}
                </Box>
            </HeroSection>

            <Container maxWidth="md" sx={{ mt: 0 }}>

                {/* How to start with this coach */}
                <Typography
                    variant="h5"
                    component="h2"
                    sx={{ fontWeight: 600, mb: 3, textAlign: "center" }}
                >
                    Here's how we make it easy to start with Coach {coach.first_name || displayName}
                </Typography>

                <List disablePadding>
                    {/* Step 1: Assessment */}
                    <ListItem alignItems="flex-start" sx={{ px: 0, py: 2 }}>
                        <ListItemText
                            primary={
                                <Typography variant="subtitle1" fontWeight={600}>
                                    STEP 1: Complete the Confidence Assessment
                                </Typography>
                            }
                            secondary={
                                <>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        Every athlete begins here. Our 50-question assessment uncovers mindset,
                                        strengths, and growth areas so Coach {coach.first_name || displayName} can
                                        tailor training.
                                    </Typography>
                                    <Button
                                        component={Link}
                                        to="/account/subscriptions"
                                        variant="contained"
                                        size="small"
                                        sx={{ mt: 1 }}
                                    >
                                        View Membership Plans
                                    </Button>
                                </>
                            }
                        />
                    </ListItem>

                    {/* Step 2: Report */}
                    <ListItem alignItems="flex-start" sx={{ px: 0, py: 2 }}>
                        <ListItemText
                            primary={
                                <Typography variant="subtitle1" fontWeight={600}>
                                    STEP 2: Get Your Personalized Report
                                </Typography>
                            }
                            secondary={
                                <>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        You’ll receive a clear, parent-friendly breakdown of your athlete’s
                                        confidence profile and recommendations for coaching focus.
                                    </Typography>
                                    <Button
                                        component={Link}
                                        to="https://static1.squarespace.com/static/68ba018e57c088379c44a42d/t/68c362d16b5b8678893a384c/1757635281598/Sample++Confidence++Report+.pdf"
                                        target="_blank"
                                        variant="outlined"
                                        size="small"
                                        sx={{ mt: 1 }}
                                    >
                                        See a Sample Report
                                    </Button>
                                </>
                            }
                        />
                    </ListItem>

                    {/* Step 3: Coaching options */}
                    <ListItem alignItems="flex-start" sx={{ px: 0, py: 2 }}>
                        <ListItemText
                            primary={
                                <Typography variant="subtitle1" fontWeight={600}>
                                    STEP 3: Choose Your Coaching Option with Coach {coach.first_name || displayName}
                                </Typography>
                            }
                            secondary={
                                <>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        One membership covers the whole journey — monthly or annual, with a
                                        7-day free trial.
                                    </Typography>
                                    <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
                                        <Button component={Link} to="/account/subscriptions" variant="outlined" size="small">
                                            View Membership Plans
                                        </Button>
                                    </Box>
                                </>
                            }
                        />
                    </ListItem>

                    {/* Step 4: Watch confidence grow */}
                    <ListItem alignItems="flex-start" sx={{ px: 0, py: 2 }}>
                        <ListItemText
                            primary={
                                <Typography variant="subtitle1" fontWeight={600}>
                                    STEP 4: Watch Confidence Grow
                                </Typography>
                            }
                            secondary={
                                <>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        Session by session, you’ll see stronger focus, resilience, and
                                        leadership—on the court and in life.
                                    </Typography>
                                </>
                            }
                        />
                    </ListItem>
                </List>

                {/* Not ready CTA */}
                <Card sx={{ mt: 4 }}>
                    <CardContent sx={{ textAlign: "center", py: 4 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            Not ready to purchase yet?
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            We’d love to talk first.
                        </Typography>
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<EmailIcon />}
                            component={Link}
                            to="/contact"
                            sx={{ px: 4, py: 1.5, fontWeight: 600 }}
                        >
                            Talk to Us First
                        </Button>
                    </CardContent>
                </Card>
            </Container>
        </Box>
    );
};

export default CoachProfileScreen;
